<template>
  <div class="passwords-container">
    <v-checkbox
      v-model="passwordFlag"
      label="Password required to access link"
      @change="$emit('onEventPasswordsFlagChange', passwordFlag)"
    />
    <p class="passwords-description">
      If enabled attendees will have to enter a password you set up before being able to
      enter the registration page.
    </p>

    <v-expand-transition>
      <div v-if="eventPasswordsFlag" style="display: flex; flex-direction: column; gap: 1rem;">
        <div v-for="eventPassword in eventPasswords" :key="eventPassword.id" class="event-password-item">
          <div>
            <div class="copy">
              <div class="truncate" :title="eventPassword.password">
                {{ eventPassword.password }}
              </div>
              <div>
                <button
                  v-clipboard:copy="eventPassword.password"
                  type="button"
                  :style="{color: '#0277BD'}"
                >
                  <span>
                    <v-icon class="copy_icon" :style="{color: '#0277BD'}">
                      content_copy
                    </v-icon>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div class="password-date-container">
            <div v-if="eventPassword.isExpired">
              expired
            </div>
            <div v-else>
              {{ getTimeUntilExpiresAsString(eventPassword.expiresAt) }}
            </div>
          </div>
          <div class="icon-container">
            <v-icon
              v-if="!eventPassword.isExpired"
              color="primary"
              class="icon"
              @click="editPassword(eventPassword)"
            >
              edit_outline
            </v-icon>
            <v-icon
              color="error"
              class="icon"
              @click="onDeletePasswordDialog(eventPassword)"
            >
              delete_outline
            </v-icon>
          </div>
        </div>
        <div>
          <main-button
            outline
            color="primary"
            :disabled="nonSavedEventPasswordExists"
            @click="modalOpen = true"
          >
            <v-icon style="margin-right: 0.5rem">add</v-icon>
            Add new password
          </main-button>
        </div>
      </div>
    </v-expand-transition>
    <event-password-delete-confirm-modal
      v-if="isDeleteModalOpen"
      :event-password="eventPasswordToDelete"
      @close="closeDeleteConfirmModal"
      @submit="deleteEventPassword"
    />
    <event-password-form-modal
      v-if="modalOpen"
      :show.sync="modalOpen"
      :title="modalTitle"
      :password="passwordToEdit"
      @onClearEditPassword="onClearEdit"
      @onDeletePassword="onDeletePasswordDialog"
      @onCreate="createEventPassword"
      @onUpdate="updateEventPassword"
      @onCopyPassword="copyEventPassword"
    />
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>
<script>
import {
  CREATE_EVENT_PASSWORD,
  DELETE_EVENT_PASSWORD,
  GET_ALL_EVENT_PASSWORDS,
  UPDATE_EVENT_PASSWORD,
} from '@/graphql/eventPasswords.graphql'
import EventPasswordDeleteConfirmModal from '@/components/events/event-password-delete-confirm-modal.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { defaultMessages, getTimeUntilExpiresAsString, getValidationErrors } from '@/utils'
import EventPasswordFormModal from '@/components/organizations/event-password-form-modal.vue'

export default {
  name: 'event-passwords',
  components: { EventPasswordFormModal, SimpleSnackbar, EventPasswordDeleteConfirmModal },
  props: {
    eventId: String,
    eventPasswordsFlag: Boolean,
  },
  data() {
    return {
      passwordFlag: this.eventPasswordsFlag,
      eventPasswords: [],
      isDeleteModalOpen: false,
      eventPasswordToDelete: null,
      passwordToEdit: null,
      snackbar: null,
      modalOpen: false,
      errorMessages: {
        required: 'This field is required.',
        maxLength: 'Maximum 30 characters allowed.',
        minLength: 'Enter at least 6 characters.',
      },
    }
  },
  computed: {
    modalTitle() {
      return this.passwordToEdit ? 'Edit' : 'Create a new Password'
    },
    nonSavedEventPasswordExists() {
      return this.eventPasswords.some(eventPassword => !eventPassword.id)
    },
  },
  async mounted() {
    await this.fetchEventPasswords()
  },
  methods: {
    getTimeUntilExpiresAsString,
    onDeletePasswordDialog(password) {
      this.openDeleteConfirmModal(password)
    },
    onClearEdit() {
      this.passwordToEdit = null
    },
    editPassword(eventPassword) {
      this.passwordToEdit = eventPassword
      this.modalOpen = true
    },
    getErrors: function(field) {
      return getValidationErrors({
        ...defaultMessages,
        ...this.errorMessages,
      })(field)
    },
    async copyEventPassword(password) {
      await navigator.clipboard.writeText(password)
      this.snackbar = {
        open: true,
        messageProp: 'Copied to clipboard',
        color: 'primary',
      }
    },
    async fetchEventPasswords() {
      const { data: { getAllPasswordsByEventId } } = await this.$apollo.query({
        query: GET_ALL_EVENT_PASSWORDS,
        variables: {
          eventId: this.eventId,
        },
      })

      this.eventPasswords = getAllPasswordsByEventId
    },
    async updateEventPassword(eventPassword) {
      await this.$apollo.mutate({
        mutation: UPDATE_EVENT_PASSWORD,
        variables: {
          eventId: this.eventId,
          id: eventPassword.id,
          password: eventPassword.password,
          expiresAt: eventPassword.expiresAt,
        },
      })
      await this.fetchEventPasswords()
    },
    async createEventPassword(eventPassword) {
      await this.$apollo.mutate({
        mutation: CREATE_EVENT_PASSWORD,
        variables: {
          eventId: this.eventId,
          password: eventPassword.password,
          expiresAt: eventPassword.expiresAt,
        },
      })
      await this.fetchEventPasswords()
    },
    async deleteEventPassword(id) {
      await this.$apollo.mutate({
        mutation: DELETE_EVENT_PASSWORD,
        variables: {
          eventId: this.eventId,
          id,
        },
      })

      this.isDeleteModalOpen = false

      await this.fetchEventPasswords()

      // Close the modal if its getting deleted from inside the modal
      if (this.modalOpen) {
        this.modalOpen = false
        this.passwordToEdit = null
      }

      if (!this.eventPasswords.length) {
        this.passwordFlag = false
        this.$emit('onEventPasswordsFlagChange', this.passwordFlag)
      }

    },
    openDeleteConfirmModal(eventPassword) {
      if (!eventPassword.id) {
        this.eventPasswords = this.eventPasswords.filter(item => item.id !== eventPassword.id)

        return
      }
      this.eventPasswordToDelete = eventPassword
      this.isDeleteModalOpen = true
    },
    closeDeleteConfirmModal() {
      this.eventPasswordToDelete = null
      this.isDeleteModalOpen = false
    },
  },
}
</script>


<style scoped>
::v-deep(.custom-password-field .v-input__slot) {
  box-shadow: none !important;
  border-radius: 10px 10px 10px 10px !important;
  background: #E6F1F8 !important;
  color: #0277BD !important;
}

::v-deep(.custom-password-field .v-text-field__slot input) {
  color: #0277BD !important;
}

::v-deep(.custom-password-field .v-text-field__slot label) {
  color: #0277BD !important;
}

::v-deep(.custom-password-field .v-icon.v-icon.v-icon--link) {
  color: #0277BD !important;
}

.passwords-container {
  background-color: white;
  border: 1px solid #D9D9D9;
  border-radius: 10px 10px 10px 10px;
  width: 100%;
  max-width: 488px;
  padding: 20px;
  margin: 3rem 0;

  .v-input--checkbox {
    margin: 0;
  }

  .v-input--checkbox ::v-deep .v-messages {
    display: none;
  }

  .v-input--checkbox ::v-deep .v-input__slot {
    margin: 0;
  }

  .v-input--checkbox ::v-deep .v-label {
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 1.125rem;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
  }

  .passwords-description {
    margin: 0.5rem 0 1.5rem 2rem;
    color: #979797;
    font-family: "Source Sans Pro";
    font-size: 0.875rem;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
  }

  .event-password-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    width: 100%;
    gap: 1rem;
  }

  .copy {
    padding: 10px 15px;
    background: #E6F1F8;
    color: #0277BD;
    display: flex;
    align-items: center;
    gap: 20px;
    border-radius: 10px;
    justify-content: space-between;
  }

  .password-date-container {
    color: #979797;
    font-size: 16px;
    font-family: Roboto;
    font-weight: 400;
    white-space: nowrap;
  }

  .icon-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .icon {
    width: 20px;
    height: 20px;
    display: inline-block;
  }

  .truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: default;
  }
}
</style>
